import React from "react";
import { Box, useTheme } from "@mui/material";
import Block from "@mui/icons-material/Block";
import { DotLegalLink, ToDto } from "@dotlegal/dotlegal-ui-components";

interface RiskLevelChipProps {
    level: number | null;
    label: string;
    color: { backgroundColor: string; color: string; borderColor?: string };
    icon: React.ReactNode;
    // Avoided scenarios are off the risk scale — the source was eliminated, not reduced — so the "after" chip
    // is a neutral grey pill with a block glyph instead of a coloured level.
    eliminated?: boolean;
    // When the chip sits next to a link to the same destination, the chip itself becomes part of that link.
    linkTo?: ToDto | string;
}

function RiskLevelChip({ level, label, color, icon, eliminated = false, linkTo }: RiskLevelChipProps) {
    const theme = useTheme();

    if (level == null) return null;

    // Indicator-driven chips supply a borderColor and read as the brand navy; callers without configured
    // indicators (e.g. the legal-entity risk dialog) pass no borderColor and keep their own text color.
    const textColor = color.borderColor ? theme.palette.primary.dark : color.color;

    const chip = (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                paddingX: 1,
                paddingY: "3px",
                borderRadius: "12px",
                fontSize: "0.75rem",
                fontWeight: 600,
                lineHeight: 1.4,
                whiteSpace: "nowrap",
                backgroundColor: eliminated ? theme.palette.grey[200] : color.backgroundColor,
                color: eliminated ? theme.palette.text.secondary : textColor,
                border: !eliminated && color.borderColor ? `1px solid ${color.borderColor}` : undefined,
                "& svg": { display: "block" },
            }}
        >
            {eliminated ? <Block sx={{ fontSize: "0.9rem" }} /> : icon}
            {label}
        </Box>
    );

    if (!linkTo) return chip;

    return (
        <DotLegalLink to={linkTo} noUnderlineOnHover stopPropagation>
            {chip}
        </DotLegalLink>
    );
}

export default RiskLevelChip;
